<template>
  <div
    ref="session"
    class="session cms-session"
    :class="{
      'session-inline': layoutType === SessionLayoutType.Inline,
      'session-wide': layoutType === SessionLayoutType.Wide,
      'loading': isLoading,
      'finished-mapping': isFinishedStrengthsMapping,
      'finished-indicator-mapping': isFinishedIndicatorMapping,
    }"
  >
    <div v-if="doc.tags.includes('BETA')">
      <Tooltip
        text="This session is a BETA feature, meaning we are still ironing out any rough edges"
        custom-class="beta-sticker-tooltip"
        :enterable="false"
      >
        <div class="beta-sticker-tooltip-target" />
      </Tooltip>
      <img class="beta-sticker" src="../../assets/beta-sticker.svg" alt="Gyre BETA sticker" />
    </div>

    <div
      class="session-background"
      :style="{ 'background-image': 'url(' + sessionBackgroundImageSrc + ')' }"
    >
      <!-- Include an image as well as the background to ensure the image is pre-loaded -->
      <img :src="sessionBackgroundImageSrc" alt="session background" />
    </div>

    <HomeNavigation :closeUrl="closeUrl" :display="closeButtonDisplay" ref="homeNavigation" />
    <TitleVisuals
      ref="titleVisuals"
      :session-title="sessionTitle"
      :session-logo-image-src="sessionLogoImageSrc"
    />

    <div class="background-visuals">
      <AbsoluteVisual ref="fadedLogoVisual">
        <img
          src="../../assets/sessions/session-1/cs-logo-faded.png"
          alt="faded logo"
          style="width: 55%; left: 21%; top: 18%"
        />
      </AbsoluteVisual>
    </div>

    <div ref="textWrapper" class="text-wrapper">
      <div id="titleSlide" class="slide-wrapper spacer-slide">
        <!-- Empty space for title display -->
      </div>

      <!--   SLICES   -->
      <div v-for="(slice, index) in slices" :key="index">
        <TextAndImageSlide
          v-if="slice.prismicSlice.slice_type === 'cms-session-slide-text-and-image'"
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-and-image-slide"
        />

        <TextSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-text'"
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-slide"
        />

        <TextImageAndButtonSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-button'"
          :session-type="sessionType"
          :slice="slice"
          :index="index"
          :doc="doc"
          :close-url="closeUrl"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-image-and-button-slide"
        />

        <TextImageAndButtonSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-complete-button'"
          has-complete-button
          :session-type="sessionType"
          :level-number="currentLevelNumber"
          :doc="doc"
          :slice="slice"
          :close-url="closeUrl"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-image-and-complete-button-slide"
        />

        <!--TODO: This component needs reviewing-->
        <ControlSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-control-slide'"
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="control-slide"
        />

        <ChecklistSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-checklist'"
          :slice="slice"
          :index="index"
          :sessionDetails="sessionQuestionnaireDetails"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="checklist-slide"
        />

        <TextAndLottieSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-text-and-lottie'"
          :slice="slice"
          :reverse-lottie-url="getLottieUrlFromSlice(slices[index + 1])"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-and-lottie-slide"
        />

        <TextButtonAndLottieSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-button-and-lottie'"
          :session-type="sessionType"
          :doc="doc"
          :slice="slice"
          :reverse-lottie-url="getLottieUrlFromSlice(slices[index + 1])"
          :index="index"
          :close-url="closeUrl"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-button-and-lottie-slide"
        />

        <TextButtonAndLottieSlide
          v-else-if="
            slice.prismicSlice.slice_type === 'cms-session-slide-with-complete-button-and-lottie'
          "
          has-complete-button
          :session-type="sessionType"
          :level-number="currentLevelNumber"
          :doc="doc"
          :slice="slice"
          :reverse-lottie-url="getLottieUrlFromSlice(slices[index + 1])"
          :index="index"
          :close-url="closeUrl"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="text-complete-button-and-lottie-slide"
        />

        <DropdownSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-dropdown'"
          :slice="slice"
          :index="index"
          :sessionDetails="sessionQuestionnaireDetails"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="dropdown-slide"
        />

        <DataCaptureSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-with-data-capture'"
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="data-capture-slide"
        />

        <FullscreenLottieSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-session-slide-fullscreen-lottie'"
          :slice="slice"
          :reverse-lottie-url="getLottieUrlFromSlice(slices[index + 1])"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="fullscreen-lottie-slide"
        />

        <StrengthsMappingSlide
          v-else-if="slice.prismicSlice.slice_type === 'cms-strength-mapping'"
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          :can-re-map-strengths="!isFoundationsPathway"
          @onCollapse="handleSlideCollapsing"
          @onIndicatorCollapse="handleSlideCollapsing"
          data-test="strength-mapping-slide"
        />

        <HeadSlide
          v-else-if="
            slice.prismicSlice.slice_type === 'cms-session-slide-text-and-solo-head' ||
            slice.prismicSlice.slice_type === 'cms-session-slide-text-and-head'
          "
          :slice="slice"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
          data-test="head-slide"
        />

        <FinalPathwayStepSlide
          v-else-if="doc.type === 'cms-pathway-step' || doc.type === 'cms-personal-pathway-step'"
          :isAfterStrengthMapping="strengthMappingIndex > -1 && index > strengthMappingIndex"
          :doc="doc"
          :index="index"
          :ref="(el) => setDynamicRef(el, index)"
        />

        <div v-else :ref="(el) => setDynamicRef(el, index)" />
      </div>
      <!--   END OF SLICES   -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { Power0, Power2, TimelineMax, TweenLite, TweenMax } from "gsap";
import * as ScrollMagic from "scrollmagic";
import { StartEventParameters } from "scrollmagic";
// TODO: dont think we need this import line anymore???
// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved
// import "imports-loader?define=>false!scrollmagic/scrollmagic/uncompressed/plugins/animation.gsap"; // Required in order to get ScrollMagic + GSAP working - see https://github.com/janpaepke/ScrollMagic/issues/665
import { ScrollMagicPluginGsap } from "scrollmagic-plugin-gsap";
import Debug from "debug";
import { Tooltip } from "uiv";
import {
  ComponentPublicInstance,
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useRoute } from "vue-router";
import pinia from "../../stores";
import cmsContentMap from "../../services/cms/cms-content-map";
import SessionInstanceState from "../../components/sessions/session-instance-state";
import { csContext } from "../../services/cs-context";
import HomeNavigation from "../../components/sessions/home-navigation.vue";
import TitleVisuals from "../../components/sessions/title-visuals.vue";
import AbsoluteVisual from "../../components/sessions/absolute-visual.vue";
import CmsImg from "../../components/cms/cms-img.vue";
import TextAndImageSlide from "../../components/cms/session-slides/text-and-image-slide.vue";
import cms from "../../services/cms/cms";
import {
  CmsDocument,
  CmsPathwayStepFinalSlide,
  CmsPersonalPathwayStep,
  CmsPersonalPathwayStepData,
  CmsSessionSlideWithButtonTextAndLottie,
  CmsSessionSlideWithFullScreenLottie,
  CmsSessionSlideWithTextAndLottie,
  CmsSessionWithSlides,
  CmsTeamPathwayStep,
  CmsTeamPathwayStepData,
  PrismicSlice,
} from "../../services/cms/cms-type-definitions";
import TextImageAndButtonSlide from "../../components/cms/session-slides/text-image-and-button-slide.vue";
import ControlSlide from "../../components/cms/session-slides/control-slide.vue";
import { SessionDetails } from "../../composables/useSessionQuestionnaire";
import ChecklistSlide from "../../components/cms/session-slides/checklist-slide.vue";
import TextAndLottieSlide from "../../components/cms/session-slides/text-and-lottie-slide.vue";
import TextButtonAndLottieSlide from "../../components/cms/session-slides/text-button-and-lottie-slide.vue";
import DropdownSlide from "../../components/cms/session-slides/dropdown-slide.vue";
import StrengthsMappingSlide from "../../components/cms/session-slides/strengths-mapping-slide.vue";
import FinalPathwayStepSlide from "../../components/cms/session-slides/final-pathway-step-slide.vue";
import FullscreenLottieSlide from "../../components/cms/session-slides/fullscreen-lottie-slide.vue";
import TextSlide from "../../components/cms/session-slides/text-slide.vue";
import HeadSlide from "../../components/cms/session-slides/head-slide.vue";
import { useCmsSessionStore } from "../../stores/useCmsSessionStore";
import HeadFromModel from "../../components/head/head-from-model.vue";
import {
  AutoScrollingMode,
  SessionLayoutType,
} from "../../components/sessions/session-animation-controller";
import DataCaptureSlide from "../../components/cms/session-slides/data-capture-slide.vue";
import useSessionBase from "../../composables/useSessionBase";

// Not needed apparently?
// ScrollMagicPluginGsap(ScrollMagic, TweenMax, TimelineMax);

const { $ } = window as any; // jQuery

const debug = Debug("cs.CmsSession");

export type PrismicSliceWithData = {
  section: string;
  prismicSlice: PrismicSlice;
};

type PrismicSliceWithLottie =
  | CmsSessionSlideWithButtonTextAndLottie
  | CmsSessionSlideWithTextAndLottie
  | CmsSessionSlideWithFullScreenLottie;

const route = useRoute();

const currentTeamId = csContext.currentTeam?.id;

const currentLevelNumber = parseInt(route.query.levelNumber as string, 10);

const props = defineProps<{
  doc: CmsDocument | CmsSessionWithSlides | CmsTeamPathwayStep | CmsPersonalPathwayStep;
}>();

const doc = ref<CmsSessionWithSlides | CmsTeamPathwayStep | CmsPersonalPathwayStep>(
  props.doc as CmsSessionWithSlides | CmsTeamPathwayStep | CmsPersonalPathwayStep,
);

const mountingPromise = ref<Promise<void>>();
const strengthMappingIndex = ref(-1);
const indicatorMappingIndex = ref(-1);

const cmsSessionStore = useCmsSessionStore(pinia());

const isFinishedStrengthsMapping = computed(() => cmsSessionStore.hasFinishedMapping.strengths);
const isFinishedIndicatorMapping = computed(() => cmsSessionStore.hasFinishedMapping.indicators);

const closeButtonDisplay = computed(() => {
  const sessionIsALevel = !!route.query.levelTitle;
  if (sessionIsALevel) return "home";

  if (doc.value.type === "cms-personal-pathway-step") return "home";
  const closeButtonGoesTo = doc.value?.data?.close_button_goes_to;

  if (closeButtonGoesTo === "team") return "team";
  if (closeButtonGoesTo === "based-on-final-slide") return "x";
  if (closeButtonGoesTo === "log-off") return "log-off";

  return "home";
});

const closeUrl = computed(() => {
  if (doc.value.type === "cms-personal-pathway-step") return "/";
  const closeButtonGoesTo = doc.value?.data?.close_button_goes_to;

  if (closeButtonGoesTo === "team" && !!currentTeamId) {
    return `/team/dashboard/${currentTeamId}`;
  }

  if (closeButtonGoesTo === "based-on-final-slide") {
    const slide = doc.value.data.body[doc.value.data.body.length - 1];
    if ("button_link" in slide.primary) {
      return cms.linkResolver(slide.primary.button_link) ?? "/";
    }
  }
  return "/";
});

const sessionType = computed(() => doc.value.data.session_type);
const sessionRef = computed(() => {
  const match = doc.value?.href?.match(/ref=[a-zA-Z0-9]*/);
  if (match && match.length > 0) {
    return match[0].replace("ref=", "");
  }

  return "";
});

function getSessionId(): string {
  if (!doc.value || !doc.value.uid) {
    throw new Error("CMS document must be initialised with a uid, before calling getSessionId");
  }

  return doc.value.uid;
}

const instance = getCurrentInstance()?.proxy as ComponentPublicInstance;

const session = ref<HTMLElement | null>(null);

const slideRefs = ref<Record<string, any>>({});

function setDynamicRef(el, index) {
  if (el) {
    // instance.$refs[`slide${index}`] = el;
    slideRefs.value[`slide${index}`] = el;
  }
}

const {
  isInline,
  hasCompletedSession,
  initializeAfterImagesLoaded,
  initialize,
  initializeAnimations,
  isLoading,
  handleCloseSession,
  handleKeyDown,
  layoutType,
  titleTimescale,
  loadAnimations,
  titleTextEase,
  titleVisualEase,
  zoomEnter,
  scene,
  element,
  zoomLeave,
  destroyed,
  getSessionVersion,
  classScene,
  animations,
} = useSessionBase(getSessionId, instance);

function handleSlideCollapsing(sceneIndex: number): void {
  debug("handleSlideCollapsing called with index:", sceneIndex);
  animations.value!.goToScene(sceneIndex, AutoScrollingMode.Quick);
}

async function prepare(): Promise<void> {
  document.title = `${cms.asText(doc.value.data.title)} - Gyre`;
  initialize();
  initializeAnimations();
}

const wasReportOpen = ref(false);

function onKeyDown(event: KeyboardEvent): void {
  handleKeyDown(event);
}

function handlePotentialReportOpen(): void {
  if (!wasReportOpen.value && SessionInstanceState.get("reportIsOpen")) {
    wasReportOpen.value = true;
    document.removeEventListener("keydown", onKeyDown);
  } else if (wasReportOpen.value && !SessionInstanceState.get("reportIsOpen")) {
    wasReportOpen.value = false;
    document.addEventListener("keydown", onKeyDown);
  }
}

function onImagesLoaded(): void {
  // Wait for mounting to complete (so animations are initialized)
  // before completion the load
  mountingPromise.value!.then(() => {
    initializeAfterImagesLoaded();
  });
}

onMounted(async () => {
  debug("mounted", instance);

  // Get the model we need from the server
  mountingPromise.value = prepare();
  await mountingPromise.value;

  debug("DOC", doc.value);
  document.addEventListener("click", handlePotentialReportOpen);
  document.addEventListener("keydown", handlePotentialReportOpen);

  $(document).ready(() => {
    onImagesLoaded();
  });
});

onUnmounted(() => {
  debug("destroyed", getCurrentInstance()?.proxy);
  document.removeEventListener("keydown", onKeyDown);
  destroyed();
});

const isFoundationsPathway = computed(() => {
  return (
    doc.value.uid === cmsContentMap.staticRoutes.foundationsSessionUid ||
    doc.value.uid === cmsContentMap.staticRoutes.foundationsTestSessionUid ||
    doc.value.uid === cmsContentMap.staticRoutes.foundationsHackingGamesSessionUid ||
    doc.value.uid === cmsContentMap.staticRoutes.foundationsCustomFrameworkSessionUid
  );
});

function formatSlice(slice: PrismicSlice, currentIndex: number): PrismicSliceWithData {
  const section = cmsSessionStore.getSection(currentIndex);

  return {
    section,
    prismicSlice: slice,
  };
}

const finalPathwayStepSlide = computed((): CmsPathwayStepFinalSlide => {
  const pathwayStep =
    doc.value.type === "cms-pathway-step"
      ? (doc.value.data as CmsTeamPathwayStepData)
      : (doc.value.data as CmsPersonalPathwayStepData);

  return {
    slice_type: "cms-final-pathway-step-slice",
    primary: {
      text: pathwayStep.final_slide_text,
      textHtml: cms.asHtml(pathwayStep.final_slide_text).replace(/\s/g, " "),
      image: pathwayStep.final_slide_image,
      lottieFile: pathwayStep.final_slide_lottie_file,
      lottieShouldFadeOut: false,
      buttonText: pathwayStep.complete_button_text || "Complete Step",
      // leaving this undefined means that it is up to the button to decide on the link
      buttonLink: undefined,
      isCompleteButton: true,
    },
    items: [],
  };
});

const slices = computed(() => {
  if (!doc.value) return [] as PrismicSliceWithData[];

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  strengthMappingIndex.value = doc.value.data.body.findIndex((s) => {
    return s.slice_type === "cms-strength-mapping" && s.primary.questionnaire_type === "strengths";
  });

  cmsSessionStore.setStrengthMappingIndex(strengthMappingIndex.value);

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  indicatorMappingIndex.value = doc.value.data.body.findIndex((s) => {
    return s.slice_type === "cms-strength-mapping" && s.primary.questionnaire_type === "indicators";
  });

  cmsSessionStore.setIndicatorsMappingIndex(indicatorMappingIndex.value);

  const formattedSlices = doc.value.data.body.map((slice, index) => {
    return formatSlice(slice, index);
  });

  if (doc.value.type === "cms-pathway-step" || doc.value.type === "cms-personal-pathway-step") {
    const finalPathwayStepPrismicSlice = finalPathwayStepSlide.value as PrismicSlice;
    const finalStepSlideIndex = formattedSlices.length;

    const finalPathwayStepSlice = formatSlice(finalPathwayStepPrismicSlice, finalStepSlideIndex);
    formattedSlices.push(finalPathwayStepSlice);
  }

  return formattedSlices;
});

function getLottieUrlFromSlice(slice: PrismicSliceWithData): string | undefined {
  if (!slice) return undefined;
  return (slice.prismicSlice as PrismicSliceWithLottie).primary?.lottie_file?.url;
}

const sessionTitle = computed(() => (doc.value ? cms.asText(doc.value.data.title) : ""));
const sessionLogoImageSrc = computed(() => (doc.value ? doc.value.data.logo_image.url : null));
const sessionBackgroundImageSrc = computed(() =>
  doc.value ? doc.value.data.background_image.url : "",
);

const homeNavigation = ref();
const titleVisuals = ref();

function loadTitleTimeline(): () => TimelineMax {
  return () => {
    const tl = new TimelineMax({ paused: true });

    (homeNavigation.value as any).colorSwitch(tl);
    (titleVisuals.value as any).rollIn(tl, false);
    (titleVisuals.value as any).sessionLogoOut(tl, true);

    const image0 = slideRefs.value.slide0.visualDiv as HTMLDivElement;
    tl.to(image0, titleTimescale * 0.3, { autoAlpha: 1, ease: titleVisualEase.value }, 0);

    tl.to(
      element("slide0"),
      titleTimescale * 0.3,
      { autoAlpha: 1, ease: titleVisualEase.value },
      0,
    );

    return tl;
  };
}

function loadFirstSlideAnimation(titleTimeline: () => TimelineMax): void {
  const titleTriggerHookPosition = isInline.value ? 0.98 : 1;
  const titleScene = new ScrollMagic.Scene({
    triggerElement: "#slide0-inner",
    triggerHook: titleTriggerHookPosition,
  });

  const visual = (slideRefs.value.slide0 || []).visual as InstanceType<typeof CmsImg> | undefined;

  animations.value!.linkAnimationToScene(
    "title",
    titleTimeline,
    titleScene,
    undefined,
    element("slide0"),
    (event: StartEventParameters) => {
      if (visual?.playAnimation) {
        visual.playAnimation(false);
      }
    },
  );
}

function slideIncludesLottie(slice: PrismicSliceWithData): boolean {
  switch (slice.prismicSlice.slice_type) {
    case "cms-session-slide-text-and-lottie":
    case "cms-session-slide-fullscreen-lottie":
    case "cms-session-slide-with-button-and-lottie":
      return true;
    default:
      return false;
  }
}

// this is to trigger any elements which have their own animations, eg lottie, heads
function triggerVisualAnimation(
  event: StartEventParameters,
  previousVisual: InstanceType<typeof CmsImg> | InstanceType<typeof HeadFromModel>,
  visual: InstanceType<typeof CmsImg> | InstanceType<typeof HeadFromModel>,
): void {
  if (event.scrollDirection === "REVERSE" && previousVisual?.playAnimation) {
    const isLottie = previousVisual?.$el?.className === "lottie-container";
    if (isLottie) {
      previousVisual.playAnimation(true);
    }
  }
  if (event.scrollDirection === "FORWARD" && visual?.playAnimation) {
    visual.playAnimation(false);
  }
}

function applyGenericAnimationToSlide(slice: PrismicSliceWithData, previousIndex: number): void {
  const timescale = 0.5;
  const i = previousIndex + 1;
  const slideId = `slide${i}`;
  const previousSlideId = `slide${i - 1}`;
  const { visualDiv } = slideRefs.value[slideId];
  const previousVisualDiv = slideRefs.value[previousSlideId].visualDiv;
  const { visual } = slideRefs.value[slideId];
  const previousVisual = slideRefs.value[previousSlideId].visual;

  scene(
    slideId,
    (tl) => {
      const previousSlide = slices.value[i - 1];
      const isLottieAndShouldNotFadeOut =
        slideIncludesLottie(previousSlide) &&
        !(previousSlide.prismicSlice as PrismicSliceWithLottie).primary.fade_out_transition;

      if (previousVisualDiv) {
        tl.to(
          previousVisualDiv,
          isLottieAndShouldNotFadeOut ? 0.1 : timescale,
          { autoAlpha: 0 },
          0,
        );
      }

      if (visualDiv) {
        tl.to(visualDiv, isLottieAndShouldNotFadeOut ? 0.0001 : timescale, { autoAlpha: 1 }, 0);
      }
      // Include home navigation animations when there is a strength mapping slide
      if (slice.prismicSlice.slice_type === "cms-strength-mapping") {
        tl.to((homeNavigation.value as any).$el, timescale * 2, { autoAlpha: 0, zIndex: -1 }, 0);
      } else {
        tl.to((homeNavigation.value as any).$el, timescale * 2, { autoAlpha: 1, zIndex: 104 }, 0);
      }
    },
    undefined,
    undefined,
    (event: StartEventParameters) => triggerVisualAnimation(event, previousVisual, visual),
  );
}

// Load animations for all session-slides except first
function loadGenericSlideAnimations(): void {
  slices.value.slice(1).forEach((slide, iMinusOne) => {
    applyGenericAnimationToSlide(slide, iMinusOne);
  });
}

loadAnimations.value = () => {
  debug("loadAnimations");
  TweenLite.defaultEase = Power2.easeInOut;

  document.addEventListener("keydown", onKeyDown);

  const titleTimeline = loadTitleTimeline();
  loadFirstSlideAnimation(titleTimeline);
  loadGenericSlideAnimations();
};

const sessionQuestionnaireDetails = computed<SessionDetails>(() => {
  return {
    currentTeamId,
    sessionId: getSessionId(),
    sessionRef: sessionRef.value,
  };
});

defineExpose({
  isLoading,
  getSessionId,
  getSessionVersion,
  classScene,
  isInline,
  zoomLeave,
  zoomEnter,
  titleTextEase,
  titleVisualEase,
  closeUrl,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import "../../styles/sessions/session-variables.less";
@import "../../styles/sessions/session-layout-mixins.less";
@import "../../styles/sessions/cms-session";

.beta-sticker-tooltip {
}
</style>
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.beta-sticker {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9998;
  width: 180px;

  @media (max-width: @screen-xxs-max) {
    width: 120px;
  }
}

.beta-sticker-tooltip-target {
  position: fixed;
  cursor: help;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 120px;
  height: 80px;
  border-bottom-right-radius: 50%;

  @media (max-width: @screen-xxs-max) {
    display: none;
  }
}
</style>
